import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  ChartBarIcon, 
  DocumentChartBarIcon, 
  UserGroupIcon,
  BookOpenIcon,
  PlayIcon,
  CalendarIcon,
  TrophyIcon,
  AcademicCapIcon,
  StarIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [reportData, setReportData] = useState({});
  const [paramValues, setParamValues] = useState({});
  const [quizzes, setQuizzes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState({});
  const [selectedReport, setSelectedReport] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');

  useEffect(() => {
    api.get('/reports').then(res => setReports(res.data));
    // load quizzes and users for selects
    api.get('/quizzes?limit=100').then(res => setQuizzes(res.data.data || res.data));
    api.get('/users').then(res => setUsers(res.data.data || res.data));
  }, []);

  // Report type icons mapping
  const getReportIcon = (reportId) => {
    const iconMap = {
      'quizAnalytics': ChartBarIcon,
      'studentPerformance': TrophyIcon,
      'libraryStats': BookOpenIcon,
      'courseEngagement': UserGroupIcon,
      'default': DocumentChartBarIcon
    };
    return iconMap[reportId] || iconMap.default;
  };

  // Get report category styling
  const getReportCategory = (reportId) => {
    const categories = {
      'quizAnalytics': { 
        name: 'Quiz Analytics', 
        color: 'bg-blue-500', 
        bgColor: 'bg-blue-50', 
        borderColor: 'border-blue-200',
        textColor: 'text-blue-700'
      },
      'studentPerformance': { 
        name: 'Student Performance', 
        color: 'bg-green-500', 
        bgColor: 'bg-green-50', 
        borderColor: 'border-green-200',
        textColor: 'text-green-700'
      },
      'libraryStats': { 
        name: 'Library Statistics', 
        color: 'bg-purple-500', 
        bgColor: 'bg-purple-50', 
        borderColor: 'border-purple-200',
        textColor: 'text-purple-700'
      },
      'courseEngagement': { 
        name: 'Course Engagement', 
        color: 'bg-orange-500', 
        bgColor: 'bg-orange-50', 
        borderColor: 'border-orange-200',
        textColor: 'text-orange-700'
      }
    };
    return categories[reportId] || { 
      name: 'General Report', 
      color: 'bg-gray-500', 
      bgColor: 'bg-gray-50', 
      borderColor: 'border-gray-200',
      textColor: 'text-gray-700'
    };
  };

  // Helper: render data neatly per report type
  const renderReportData = (id, data) => {
    const category = getReportCategory(id);
    
    switch(id) {
      case 'quizAnalytics':
        return (
          <div className={`mt-6 p-6 ${category.bgColor} ${category.borderColor} border rounded-xl`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <UserGroupIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Submissions</p>
                    <p className="text-2xl font-bold text-gray-900">{data.totalSubmissions}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <StarIcon className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Average Score</p>
                    <p className="text-2xl font-bold text-gray-900">{data.averageScore.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'studentPerformance':
        return (
          <div className={`mt-6 p-6 ${category.bgColor} ${category.borderColor} border rounded-xl`}>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-green-500 to-green-600">
                <h4 className="text-lg font-semibold text-white flex items-center">
                  <TrophyIcon className="w-5 h-5 mr-2" />
                  Performance History
                </h4>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quiz</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <AcademicCapIcon className="w-5 h-5 text-gray-400 mr-3" />
                            <span className="text-sm font-medium text-gray-900">{row.quizTitle}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.courseTitle}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            row.score >= 80 ? 'bg-green-100 text-green-800' :
                            row.score >= 60 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {row.score}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {new Date(row.date).toLocaleDateString()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      case 'libraryStats':
        return (
          <div className={`mt-6 p-6 ${category.bgColor} ${category.borderColor} border rounded-xl`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <BookOpenIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold text-gray-900">Total Items</h4>
                    <p className="text-3xl font-bold text-purple-600">{data.totalItems}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Items by Type</h4>
                <div className="space-y-3">
                  {data.byType.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="font-medium text-gray-700 capitalize">{item.type}</span>
                      <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">
                        {item.count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'courseEngagement':
        return (
          <div className={`mt-6 p-6 ${category.bgColor} ${category.borderColor} border rounded-xl`}>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600">
                <h4 className="text-lg font-semibold text-white flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2" />
                  Course Engagement Overview
                </h4>
              </div>
              <div className="p-6">
                <div className="grid gap-4">
                  {data.map((row, i) => (
                    <div key={i} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-center">
                        <div className="p-2 bg-orange-100 rounded-lg mr-4">
                          <BookOpenIcon className="w-5 h-5 text-orange-600" />
                        </div>
                        <span className="font-medium text-gray-900">{row.courseTitle}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-500 mr-2">Submissions:</span>
                        <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                          {row.submissions}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="mt-6 p-6 bg-gray-50 border border-gray-200 rounded-xl">
            <div className="bg-white rounded-lg p-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <DocumentChartBarIcon className="w-5 h-5 mr-2" />
                Raw Data
              </h4>
              <pre className="bg-gray-100 p-4 rounded-lg overflow-auto text-sm">
                {JSON.stringify(data, null, 2)}
              </pre>
            </div>
          </div>
        );
    }
  };

  const handleRunReport = async (reportId) => {
    setLoading(prev => ({ ...prev, [reportId]: true }));
    
    try {
      // Get the report configuration
      const report = reports.find(r => r.id === reportId);
      let rawPath = report.path;
      if (rawPath.startsWith('/api')) rawPath = rawPath.slice(4);
      
      const paramMatch = rawPath.match(/:(\w+)/);
      const needsParam = !!paramMatch;
      const paramName = paramMatch?.[1];
      const selected = paramValues[reportId] || '';
      
      let realPath = rawPath;
      if (needsParam && selected) {
        realPath = rawPath.replace(`:${paramName}`, encodeURIComponent(selected));
      }
      
      const response = await api.get(realPath);
      setReportData(rd => ({ ...rd, [reportId]: response.data }));
      setSelectedReport(reportId);
    } catch (error) {
      console.error('Error running report:', error);
      // You could add error state handling here
    } finally {
      setLoading(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const handleExportReport = async () => {
    if (!selectedReport || !reportData[selectedReport]) {
      alert('No report data to export');
      return;
    }

    try {
      const report = reports.find(r => r.id === selectedReport);
      const data = reportData[selectedReport];
      
      // Debug: Log the data structure to console
      console.log('Exporting report data:', selectedReport, data);
      
      if (exportFormat === 'csv') {
        // Export as CSV
        const csvContent = convertToCSV(selectedReport, data, report.name);
        downloadFile(csvContent, `${report.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`, 'text/csv');
      } else if (exportFormat === 'docx') {
        // Export as DOCX
        const docxContent = await convertToDOCX(selectedReport, data, report.name);
        downloadFile(docxContent, `${report.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report');
    }
  };

  const downloadFile = (content, filename, mimeType) => {
    let blob;
    
    if (filename.endsWith('.docx')) {
      // For DOCX files, we're creating HTML that Word can open
      // Use Word-compatible MIME type
      blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    } else {
      // For CSV and other formats
      blob = new Blob([content], { type: mimeType });
    }
    
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the URL object
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const convertToDOCX = async (reportId, data, reportName) => {
    // Create HTML content that can be saved as a .docx file
    // Modern browsers can handle this as a Word document
    let htmlContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${reportName}</title>
    <style>
        body { font-family: 'Times New Roman', serif; margin: 40px; line-height: 1.6; }
        h1 { color: #2563eb; text-align: center; border-bottom: 3px solid #2563eb; padding-bottom: 10px; }
        h2 { color: #1e40af; margin-top: 30px; margin-bottom: 15px; }
        h3 { color: #1e3a8a; margin-top: 20px; margin-bottom: 10px; }
        table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        th, td { border: 1px solid #d1d5db; padding: 12px; text-align: left; }
        th { background-color: #f3f4f6; font-weight: bold; }
        .metric { display: flex; justify-content: space-between; padding: 10px; background-color: #f9fafb; margin: 5px 0; border-left: 4px solid #2563eb; }
        .date { color: #6b7280; font-style: italic; margin-bottom: 30px; text-align: center; }
        .score-high { color: #059669; font-weight: bold; }
        .score-medium { color: #d97706; font-weight: bold; }
        .score-low { color: #dc2626; font-weight: bold; }
    </style>
</head>
<body>
    <h1>${reportName}</h1>
    <p class="date">Generated on: ${new Date().toLocaleString()}</p>
    
    ${generateDOCXHTMLContent(reportId, data)}
    
    <br><br>
    <hr>
    <p style="text-align: center; color: #6b7280; font-size: 12px;">
        Report generated by VLE System - ${new Date().toLocaleDateString()}
    </p>
</body>
</html>`;
    
    return htmlContent;
  };

  const generateDOCXHTMLContent = (reportId, data) => {
    let content = '';
    
    switch(reportId) {
      case 'quizAnalytics':
        content += `
          <h2>📊 Quiz Analytics Summary</h2>
          <div class="metric">
            <span><strong>Total Submissions:</strong></span>
            <span>${data.totalSubmissions || 0}</span>
          </div>
          <div class="metric">
            <span><strong>Average Score:</strong></span>
            <span class="${data.averageScore >= 80 ? 'score-high' : data.averageScore >= 60 ? 'score-medium' : 'score-low'}">${data.averageScore || 0}%</span>
          </div>`;
        break;

      case 'studentPerformance':
        content += `<h2>🏆 Student Performance Details</h2>`;
        if (Array.isArray(data) && data.length > 0) {
          content += `
            <table>
              <thead>
                <tr>
                  <th>Quiz Title</th>
                  <th>Course</th>
                  <th>Score</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>`;
          data.forEach(row => {
            const scoreClass = row.score >= 80 ? 'score-high' : row.score >= 60 ? 'score-medium' : 'score-low';
            content += `
                <tr>
                  <td>${row.quizTitle || 'N/A'}</td>
                  <td>${row.courseTitle || 'N/A'}</td>
                  <td class="${scoreClass}">${row.score || 0}%</td>
                  <td>${new Date(row.date).toLocaleDateString()}</td>
                </tr>`;
          });
          content += `
              </tbody>
            </table>`;
        } else {
          content += `<p>No performance data available.</p>`;
        }
        break;

      case 'libraryStats':
        content += `
          <h2>📚 Library Statistics</h2>
          <div class="metric">
            <span><strong>Total Items:</strong></span>
            <span>${data.totalItems || 0}</span>
          </div>`;
        if (data.byType && Array.isArray(data.byType)) {
          content += `<h3>Items by Type:</h3><table><thead><tr><th>Type</th><th>Count</th></tr></thead><tbody>`;
          data.byType.forEach(item => {
            content += `<tr><td style="text-transform: capitalize;">${item.type || 'Unknown'}</td><td>${item.count || 0}</td></tr>`;
          });
          content += `</tbody></table>`;
        }
        break;

      case 'courseEngagement':
        content += `<h2>📈 Course Engagement Report</h2>`;
        if (Array.isArray(data) && data.length > 0) {
          content += `
            <table>
              <thead>
                <tr>
                  <th>Course Title</th>
                  <th>Total Submissions</th>
                </tr>
              </thead>
              <tbody>`;
          data.forEach(row => {
            content += `
                <tr>
                  <td>${row.courseTitle || 'N/A'}</td>
                  <td>${row.submissions || 0}</td>
                </tr>`;
          });
          content += `
              </tbody>
            </table>`;
        } else {
          content += `<p>No engagement data available.</p>`;
        }
        break;

      default:
        content += `
          <h2>📋 Report Data</h2>
          <pre style="background-color: #f3f4f6; padding: 20px; border-radius: 8px; overflow-x: auto; white-space: pre-wrap;">${JSON.stringify(data, null, 2)}</pre>`;
        break;
    }
    
    return content;
  };

  const convertToCSV = (reportId, data, reportName) => {
    let csvContent = `Report: ${reportName}\n`;
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;

    switch(reportId) {
      case 'quizAnalytics':
        csvContent += 'Metric,Value\n';
        csvContent += `Total Submissions,${data.totalSubmissions || 0}\n`;
        csvContent += `Average Score,${data.averageScore || 0}%\n`;
        break;

      case 'studentPerformance':
        csvContent += 'Quiz Title,Course Title,Score,Date\n';
        if (Array.isArray(data)) {
          data.forEach(row => {
            const quizTitle = row.quizTitle || 'N/A';
            const courseTitle = row.courseTitle || 'N/A';
            const score = row.score || 0;
            const date = new Date(row.date).toLocaleDateString();
            csvContent += `"${quizTitle}","${courseTitle}",${score}%,"${date}"\n`;
          });
        }
        break;

      case 'libraryStats':
        csvContent += 'Library Statistics\n\n';
        csvContent += `Total Items,${data.totalItems || 0}\n\n`;
        csvContent += 'Items by Type\n';
        csvContent += 'Type,Count\n';
        if (data.byType && Array.isArray(data.byType)) {
          data.byType.forEach(item => {
            csvContent += `${item.type || 'Unknown'},${item.count || 0}\n`;
          });
        }
        break;

      case 'courseEngagement':
        csvContent += 'Course Title,Submissions\n';
        if (Array.isArray(data)) {
          data.forEach(row => {
            const courseTitle = row.courseTitle || 'N/A';
            const submissions = row.submissions || 0;
            csvContent += `"${courseTitle}",${submissions}\n`;
          });
        }
        break;

      default:
        // Generic handler for any data structure
        if (Array.isArray(data) && data.length > 0) {
          // If it's an array, try to extract column headers from the first item
          const headers = Object.keys(data[0]);
          csvContent += headers.join(',') + '\n';
          
          data.forEach(row => {
            const values = headers.map(header => {
              const value = row[header];
              if (typeof value === 'string') {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value || '';
            });
            csvContent += values.join(',') + '\n';
          });
        } else if (typeof data === 'object' && data !== null) {
          // If it's an object, export as key-value pairs
          csvContent += 'Property,Value\n';
          Object.entries(data).forEach(([key, value]) => {
            const formattedValue = typeof value === 'object' ? JSON.stringify(value) : value;
            csvContent += `"${key}","${formattedValue}"\n`;
          });
        } else {
          csvContent += 'Raw Data:\n';
          csvContent += JSON.stringify(data, null, 2);
        }
        break;
    }

    return csvContent;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <DocumentChartBarIcon className="w-8 h-8 text-blue-600 mr-3" />
                Analytics & Reports
              </h1>
              <p className="mt-2 text-gray-600">Generate comprehensive reports and analyze system performance</p>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <span className="text-sm font-medium text-blue-700">
                  {reports.length} Reports Available
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Reports Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2" />
                  Available Reports
                </h2>
              </div>
              
              <div className="p-4 space-y-3">
                {reports.map(report => {
                  const IconComponent = getReportIcon(report.id);
                  const category = getReportCategory(report.id);
                  const isActive = selectedReport === report.id;
                  
                  // Parse parameters
                  let rawPath = report.path;
                  if (rawPath.startsWith('/api')) rawPath = rawPath.slice(4);
                  const paramMatch = rawPath.match(/:(\w+)/);
                  const needsParam = !!paramMatch;
                  const paramName = paramMatch?.[1];
                  const selected = paramValues[report.id] || '';
                  
                  return (
                    <div 
                      key={report.id} 
                      className={`border rounded-lg p-4 transition-all duration-200 ${
                        isActive 
                          ? `${category.bgColor} ${category.borderColor} border-2` 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center">
                          <div className={`p-2 ${category.color} rounded-lg mr-3`}>
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{report.name}</h3>
                            <span className={`text-xs px-2 py-1 rounded-full ${category.bgColor} ${category.textColor}`}>
                              {category.name}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Parameter Input */}
                      {needsParam && (
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Select {paramName === 'quizId' ? 'Quiz' : 'User'}:
                          </label>
                          {paramName === 'quizId' ? (
                            <select
                              value={selected}
                              onChange={e => setParamValues(ps => ({ ...ps, [report.id]: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Choose a quiz...</option>
                              {quizzes.map(q => (
                                <option key={q.id} value={q.title}>{q.title}</option>
                              ))}
                            </select>
                          ) : (
                            <select
                              value={selected}
                              onChange={e => setParamValues(ps => ({ ...ps, [report.id]: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Choose a user...</option>
                              {users.map(u => (
                                <option key={u.id} value={u.name}>{u.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                      
                      {/* Run Button */}
                      <button
                        onClick={() => handleRunReport(report.id)}
                        disabled={(needsParam && !selected) || loading[report.id]}
                        className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center ${
                          (needsParam && !selected) || loading[report.id]
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : `${category.color} hover:opacity-90 text-white`
                        }`}
                      >
                        {loading[report.id] ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Running...
                          </>
                        ) : (
                          <>
                            <PlayIcon className="w-4 h-4 mr-2" />
                            Run Report
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {selectedReport && reportData[selectedReport] ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {(() => {
                        const IconComponent = getReportIcon(selectedReport);
                        const category = getReportCategory(selectedReport);
                        const report = reports.find(r => r.id === selectedReport);
                        return (
                          <>
                            <div className={`p-2 ${category.color} rounded-lg mr-3`}>
                              <IconComponent className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h2 className="text-lg font-semibold text-gray-900">{report?.name}</h2>
                              <p className="text-sm text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex items-center space-x-3">
                      <select
                        value={exportFormat}
                        onChange={(e) => setExportFormat(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="csv">CSV Format</option>
                        <option value="docx">Word Document</option>
                      </select>
                      <button 
                        onClick={handleExportReport}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                        Export {exportFormat.toUpperCase()}
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  {renderReportData(selectedReport, reportData[selectedReport])}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="max-w-md mx-auto">
                  <DocumentChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Report Selected</h3>
                  <p className="text-gray-500">
                    Choose a report from the sidebar to view analytics and insights. 
                    Reports provide detailed information about quiz performance, student engagement, and system usage.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
